import { createFileRoute, Link } from "@tanstack/react-router";
import { faqPageJson } from "@/lib/faq-schema";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { Flower } from "@/components/site/Flower";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import faqItems from "@/content/faq.json";
import { FaqLoadMore } from "@/components/site/FaqLoadMore";
import { medicalWebPageSchema } from "@/components/site/InsuranceProse";

const FAQ_URL = "https://virginiabariatric.com/resources/faq";
const FAQ_IMAGE = {
  url: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg",
  alt: "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice",
  width: 1200,
  height: 630,
};

export const Route = createFileRoute("/resources/faq")({

  head: () => ({
    meta: [
      { title: "FAQ | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Find answers to frequently asked questions about weight-loss treatment at Virginia Bariatric Surgery.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "FAQ | Virginia Bariatric" },
        { property: "og:description", content: "Find answers to frequently asked questions about weight-loss treatment at Virginia Bariatric Surgery." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: FAQ_URL },
        { property: "og:image", content: FAQ_IMAGE.url },
        { property: "og:image:alt", content: FAQ_IMAGE.alt },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: String(FAQ_IMAGE.width) },
        { property: "og:image:height", content: String(FAQ_IMAGE.height) },
        { property: "og:locale", content: "en_US" },
        { property: "og:site_name", content: "Virginia Bariatric Surgery" },
        { property: "article:modified_time", content: "2026-08-09" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: FAQ_IMAGE.url },
        { name: "twitter:image:alt", content: FAQ_IMAGE.alt },
        { name: "twitter:title", content: "FAQ | Virginia Bariatric" },
        { name: "twitter:description", content: "Find answers to frequently asked questions about weight-loss treatment at Virginia Bariatric Surgery." },
      ] : []),
    ],
    links: canonicalAndAlternates("/resources/faq"),
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@graph": [
            medicalWebPageSchema({
              url: FAQ_URL,
              name: "Bariatric Surgery FAQ",
              description:
                "Answers to common questions about weight-loss surgery, medications, nutrition, and recovery from Virginia Bariatric Surgery.",
              image: FAQ_IMAGE,
              hasFaq: true,
              audienceType: "Patients considering bariatric surgery or weight-loss medication",
            }),
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${FAQ_URL}#faq`,
              url: FAQ_URL,
              inLanguage: "en-US",
              isPartOf: { "@id": `${FAQ_URL}#webpage` },
              mainEntityOfPage: { "@id": `${FAQ_URL}#webpage` },
              publisher: { "@id": "https://virginiabariatric.com/#organization" },
              mainEntity: (faqItems as { id: string; q: string; a: string }[]).map((it) => ({
                "@type": "Question",
                "@id": `${FAQ_URL}#${it.id}`,
                url: `${FAQ_URL}#${it.id}`,
                name: it.q,
                inLanguage: "en-US",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: it.a,
                  url: `${FAQ_URL}#${it.id}`,
                },
              })),
            },
          ],
        }),
      },
    ] : [],
  }),

  component: FaqPage,
});

function FaqPage() {
  return (
    <>
      <PageSection className="relative pt-4 md:pt-6 pb-3 md:pb-4">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div className="text-center max-w-4xl mx-auto">
          <EditableText
            as="h1"
            contentKey="faq.hero.title"
            defaultValue="Frequently Asked Questions"
            className="text-4xl md:text-5xl font-serif"
          />
          <EditableText
            as="p"
            contentKey="faq.hero.intro"
            defaultValue="Answers to some of the most common questions we hear from patients."
            className="mt-4 md:mt-6 text-foreground/80 leading-[1.6] text-base md:text-lg"
          />

          <nav
            aria-label="FAQ quick links"
            className="mt-6 md:mt-8 p-3 md:p-4 rounded-2xl bg-brand-soft/50 text-left"
          >
            <div className="space-y-0.5">
              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium">
                Weight Loss Medications
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#meds-vs-diabetes"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Are the injectable weight-loss medications the same as the diabetes drugs?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-forever"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Will I have to take medication forever to keep the weight loss?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-vs-procedures"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    How do medications compare to a balloon, ESG, and surgery?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-insurance-cover"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Will my insurance cover Zepbound or Wegovy in Virginia?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-insurance-excluded"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    My plan excludes weight-loss drugs. Are there other ways to get covered?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-who-should-not-take"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Who should not take weight-loss medication?
                  </a>
                </li>
                <li>
                  <a href="#meds-which-prescribed" className="hover:text-brand hover:underline underline-offset-2 transition-colors">
                    Which weight-loss medications does Dr. Fitzer prescribe?
                  </a>
                </li>
                <li>
                  <a href="#meds-glp1-expected-loss" className="hover:text-brand hover:underline underline-offset-2 transition-colors">
                    How much weight can I expect to lose on a GLP-1 medication?
                  </a>
                </li>
                <li>
                  <a href="#meds-glp1-side-effects" className="hover:text-brand hover:underline underline-offset-2 transition-colors">
                    What are the most common side effects of GLP-1 medications?
                  </a>
                </li>
                <li>
                  <a href="#meds-semaglutide-prescription" className="hover:text-brand hover:underline underline-offset-2 transition-colors">
                    Do I need a prescription for semaglutide?
                  </a>
                </li>
                <li>
                  <a href="#meds-compounded-safe" className="hover:text-brand hover:underline underline-offset-2 transition-colors">
                    Is compounded semaglutide or tirzepatide safe?
                  </a>
                </li>
                <li>
                  <a href="#meds-wegovy-pill-vs-injection" className="hover:text-brand hover:underline underline-offset-2 transition-colors">
                    Should I choose the Wegovy pill or the injection?
                  </a>
                </li>
                <li>
                  <a href="#meds-semaglutide-vs-tirzepatide" className="hover:text-brand hover:underline underline-offset-2 transition-colors">
                    Semaglutide or tirzepatide &mdash; which is better?
                  </a>
                </li>
                <li>
                  <a href="#meds-zepbound-sleep-apnea" className="hover:text-brand hover:underline underline-offset-2 transition-colors">
                    Does Zepbound really treat sleep apnea?
                  </a>
                </li>
                <li>
                  <a href="#meds-glp1-after-surgery" className="hover:text-brand hover:underline underline-offset-2 transition-colors">
                    Can I take weight-loss medication after bariatric surgery?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-oral-phentermine-safe"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Is phentermine safe?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-oral-how-much-loss"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    How much weight can I lose on phentermine or Contrave?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-oral-vs-glp1"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Do these medications work as well as Ozempic or Zepbound?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-oral-phentermine-vs-diethylpropion"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    What&apos;s the difference between phentermine and diethylpropion?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-oral-online-sites"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Can I just get phentermine from an online weight-loss site?
                  </a>
                </li>
                <li>
                  <a
                    href="#meds-oral-which-is-right"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Which weight-loss medication is right for me?
                  </a>
                </li>
              </ul>
              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium">
                Bariatric Surgery
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#what-is-bariatric-surgery"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    What is bariatric surgery?
                  </a>
                </li>
                <li>
                  <a
                    href="#what-types-of-bariatric-procedures-do-you-offer"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    What types of bariatric procedures do you offer?
                  </a>
                </li>
                <li>
                  <a
                    href="#which-procedure-is-right-for-me"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Which procedure is right for me?
                  </a>
                </li>
                <li>
                  <a
                    href="#who-qualifies-for-bariatric-surgery"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Who qualifies for bariatric surgery?
                  </a>
                </li>
                <li>
                  <a
                    href="#reasons-not-qualify"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Are there reasons I might not qualify?
                  </a>
                </li>
                <li>
                  <a
                    href="#age-limit"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Is there an age limit?
                  </a>
                </li>
              </ul>
              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium mt-1.5">
                Before Surgery
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#pre-surgical-process"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    What does the pre-surgical process look like?
                  </a>
                </li>
                <li>
                  <a
                    href="#psychological-evaluation"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Why do I need a psychological evaluation?
                  </a>
                </li>
                <li>
                  <a
                    href="#special-diet-before-surgery"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Do I have to follow a special diet before surgery?
                  </a>
                </li>
                <li>
                  <a
                    href="#stop-medications"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Will I need to stop any medications before surgery?
                  </a>
                </li>
              </ul>
              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium mt-1.5">
                The Procedure
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#laparoscopic"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Is bariatric surgery performed laparoscopically?
                  </a>
                </li>
                <li>
                  <a
                    href="#surgery-duration"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    How long does the surgery take?
                  </a>
                </li>
                <li>
                  <a
                    href="#general-anesthesia"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Will I be under general anesthesia?
                  </a>
                </li>
                <li>
                  <a
                    href="#hospital-stay"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    How long will I be in the hospital?
                  </a>
                </li>
              </ul>
              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium mt-1.5">
                Insurance &amp; Coverage
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#does-my-health-insurance-cover-bariatric-surgery"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Does my health insurance cover bariatric surgery?
                  </a>
                </li>
                <li>
                  <a
                    href="#are-the-qualifications-different-if-using-insurance"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Are the qualifications different if using insurance?
                  </a>
                </li>
              </ul>
              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium mt-1.5">
                Recovery
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#activity-restrictions"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    What are the activity restrictions during recovery?
                  </a>
                </li>
                <li>
                  <a
                    href="#how-much-pain"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    How much pain should I expect?
                  </a>
                </li>
              </ul>
              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium mt-1.5">
                Diet &amp; Nutrition After Surgery
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#what-can-i-eat-after-surgery"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    What will I be able to eat after surgery?
                  </a>
                </li>
                <li>
                  <a
                    href="#do-i-need-vitamins-forever"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Do I need to take vitamins forever?
                  </a>
                </li>
                <li>
                  <a
                    href="#what-is-dumping-syndrome"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    What is dumping syndrome?
                  </a>
                </li>
                <li>
                  <a
                    href="#will-i-ever-eat-normally-again"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Will I ever be able to eat normally again?
                  </a>
                </li>
              </ul>
              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium mt-1.5">
                Gastric Sleeve Surgery
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#sleeve-bypassed-intestine"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Is any part of my stomach or intestine bypassed?
                  </a>
                </li>
                <li>
                  <a
                    href="#sleeve-or-bypass"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Sleeve or bypass — which is better?
                  </a>
                </li>
                <li>
                  <a
                    href="#sleeve-reversible"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Can the sleeve be reversed?
                  </a>
                </li>
                <li>
                  <a
                    href="#sleeve-recovery"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    How long is recovery after gastric sleeve surgery?
                  </a>
                </li>
                <li>
                  <a
                    href="#sleeve-insurance"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Is gastric sleeve covered by insurance?
                  </a>
                </li>
                <li>
                  <a
                    href="#sleeve-diet"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    What diet do I follow after sleeve gastrectomy?
                  </a>
                </li>
              </ul>
              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium mt-1.5">
                Endoscopic Sleeve Gastroplasty
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#esg-vs-gastric-sleeve"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Is ESG the same as gastric sleeve surgery?
                  </a>
                </li>
                <li>
                  <a
                    href="#esg-vs-medications"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    How does ESG compare to weight-loss medications?
                  </a>
                </li>
                <li>
                  <a
                    href="#esg-reversible"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Is the procedure reversible?
                  </a>
                </li>
                <li>
                  <a
                    href="#esg-results"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    How long until I see results?
                  </a>
                </li>
              </ul>
              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium mt-1.5">
                Orbera&reg; Balloon
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#orbera-vs-sleeve-esg"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    How is the balloon different from gastric sleeve or ESG?
                  </a>
                </li>
                <li>
                  <a
                    href="#orbera-permanent"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Does the balloon stay in permanently?
                  </a>
                </li>
                <li>
                  <a
                    href="#orbera-feel"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Will I feel the balloon?
                  </a>
                </li>
                <li>
                  <a
                    href="#orbera-after-removal"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    What happens after the balloon is removed?
                  </a>
                </li>
              </ul>

              <p className="text-xs uppercase tracking-[0.1em] text-foreground/60 font-medium mt-1.5">
                Revisions
              </p>
              <ul className="space-y-0 text-foreground/90">
                <li>
                  <a
                    href="#revision-failed"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Does needing a revision mean my first surgery failed?
                  </a>
                </li>
                <li>
                  <a
                    href="#revision-reflux"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Will a revision help my acid reflux?
                  </a>
                </li>
                <li>
                  <a
                    href="#revision-risk"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Is revisional surgery riskier than my first operation?
                  </a>
                </li>
                <li>
                  <a
                    href="#revision-alternatives"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    Could medications or an endoscopic procedure work instead?
                  </a>
                </li>
                <li>
                  <a
                    href="#revision-weight-loss"
                    className="hover:text-brand hover:underline underline-offset-2 transition-colors"
                  >
                    How much weight can I expect to lose?
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </div>
      </PageSection>

      <PageSection className="pt-4 pb-8">
        <div className="max-w-3xl mx-auto space-y-8">
          <section>
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              Weight Loss Medications
            </h2>
            <FaqLoadMore groupLabel="weight loss medication" initialCount={8} step={8}>
              <div id="meds-vs-diabetes" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Are the injectable weight-loss medications the same as the diabetes drugs I&apos;ve heard about?
                </h3>
                <p>
                  They overlap. Semaglutide and tirzepatide were first developed for type 2 diabetes
                  (Ozempic, Mounjaro) and later approved at weight-management doses under different
                  brand names (Wegovy, Zepbound). The active medication is the same class, but the
                  approved indication and dosing differ.
                </p>
              </div>

              <div id="meds-forever" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Will I have to take medication forever to keep the weight loss?
                </h3>
                <p>
                  In short, yes. Obesity is a chronic condition, and these medications treat that
                  condition as long as they are being taken. Most people regain weight after stopping,
                  so they are designed for long-term use. Your physician will discuss a realistic
                  long-term plan with you.
                </p>
              </div>

              <div id="meds-vs-procedures" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  How do medications compare to a balloon, ESG, and surgery?
                </h3>
                <p>
                  Newer medications can rival endoscopic procedures and approach some surgical
                  results, but they require ongoing use and cost. Balloons and surgery are procedures
                  with their own risks and benefits. The easiest path to goal weight sometimes
                  combines both approaches. Dr. Fitzer will help you understand the options and the
                  outlook.
                </p>
              </div>

              <div id="meds-insurance-cover" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Will my insurance cover Zepbound or Wegovy in Virginia?
                </h3>
                <p>
                  It depends on two separate gates. First, does your plan cover weight-loss
                  medications at all? Many Virginia employer plans, and all individual and small-group
                  marketplace plans, exclude them outright. Second, if the category is covered, do you
                  meet the plan&apos;s clinical prior-authorization criteria — usually a BMI of 30 or
                  higher, or 27 or higher with a weight-related condition, plus documented lifestyle
                  efforts. Ask your plan about the exclusion question first, because no documentation
                  can overcome a benefit exclusion.{" "}
                  <Link
                    to="/medical-weight-loss/insurance"
                    className="text-brand-mid underline"
                  >
                    See our Virginia weight-loss medication insurance guide for plan-by-plan details.
                  </Link>
                </p>
              </div>

              <div id="meds-insurance-excluded" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  My plan excludes weight-loss drugs. Are there other ways to get covered?
                </h3>
                <p>
                  Yes. Some patients have a backdoor available through alternate diagnoses such as
                  type 2 diabetes, moderate-to-severe obstructive sleep apnea, established
                  cardiovascular disease, or MASH with fibrosis. Coverage follows the diagnosis, not
                  the molecule, so a drug like Mounjaro or Ozempic may be covered under a diabetes
                  indication even when the same molecule sold as Zepbound or Wegovy is excluded for
                  weight loss.{" "}
                  <Link
                    to="/medical-weight-loss/insurance"
                    hash="two-gates"
                    className="text-brand-mid underline"
                  >
                    See our Virginia weight-loss medication insurance guide for the full backdoor explanation.
                  </Link>
                </p>
              </div>

              <div id="meds-who-should-not-take" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Who should not take weight-loss medication?
                </h3>
                <p>
                  Semaglutide and tirzepatide both carry a boxed warning regarding thyroid C-cell
                  tumors, based on findings in rodents; whether that risk translates to humans is not
                  known. They are contraindicated if you have a personal or family history of
                  medullary thyroid carcinoma, Multiple Endocrine Neoplasia syndrome type 2, or a
                  serious allergy to the drug. Tell us before starting if you are pregnant,
                  breastfeeding, or planning pregnancy, or have a history of pancreatitis,
                  gallbladder disease, severe gastroparesis, an eating disorder, diabetic
                  retinopathy, or kidney problems. The most common side effects are gastrointestinal
                  and are usually most noticeable during dose escalation.
                </p>
              </div>

              <div id="meds-which-prescribed" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Which weight-loss medications does Dr. Fitzer prescribe?{" "}
                  <a
                    href="#meds-which-prescribed"
                    aria-label="Link to this question: Which weight-loss medications does Dr. Fitzer prescribe?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  The full range of FDA-approved options: semaglutide (Wegovy, Ozempic), tirzepatide (Zepbound, Mounjaro), and oral medications such as phentermine, Contrave, orlistat, and diethylpropion. The choice depends on your health history, goals, insurance, and tolerance.{" "}
                  <Link to="/medical-weight-loss/medications" className="text-brand-mid underline">See our weight loss medications introduction for the full overview.</Link>{" "}
                </p>
              </div>

              <div id="meds-glp1-expected-loss" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  How much weight can I expect to lose on a GLP-1 medication?{" "}
                  <a
                    href="#meds-glp1-expected-loss"
                    aria-label="Link to this question: How much weight can I expect to lose on a GLP-1 medication?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  In the major trials, average total body weight loss was roughly 15% with{" "}
                  <Link to="/medical-weight-loss/semaglutide" className="text-brand-mid underline">semaglutide</Link>{" "}
                  and about 20% with{" "}
                  <Link to="/medical-weight-loss/tirzepatide" className="text-brand-mid underline">tirzepatide</Link>{" "}
                  over 68 to 72 weeks, alongside diet and activity changes. Individual results vary widely. See our semaglutide and tirzepatide pages for the trial details.
                </p>
              </div>

              <div id="meds-glp1-side-effects" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  What are the most common side effects of GLP-1 medications?{" "}
                  <a
                    href="#meds-glp1-side-effects"
                    aria-label="Link to this question: What are the most common side effects of GLP-1 medications?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  Nausea, diarrhea, constipation, and vomiting are the most common. They are usually mild to moderate, appear during dose increases, and often ease with time and slower titration.{" "}
                  <Link to="/medical-weight-loss/medications" className="text-brand-mid underline">Our weight loss medications introduction explains how we manage titration.</Link>{" "}
                </p>
              </div>

              <div id="meds-semaglutide-prescription" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Do I need a prescription for semaglutide?{" "}
                  <a
                    href="#meds-semaglutide-prescription"
                    aria-label="Link to this question: Do I need a prescription for semaglutide?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  Yes. It starts with a medical evaluation — in person in Herndon or by telehealth — covering your history, labs, prior weight-loss attempts, and goals. If semaglutide is appropriate, we prescribe it and follow you closely through dosing.{" "}
                  <Link to="/medical-weight-loss/semaglutide" className="text-brand-mid underline">Full details are on our semaglutide page.</Link>{" "}
                </p>
              </div>

              <div id="meds-compounded-safe" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Is compounded semaglutide or tirzepatide safe?{" "}
                  <a
                    href="#meds-compounded-safe"
                    aria-label="Link to this question: Is compounded semaglutide or tirzepatide safe?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  We prescribe only FDA-approved{" "}
                  <Link to="/medical-weight-loss/semaglutide" className="text-brand-mid underline">semaglutide</Link>{" "}
                  (Wegovy, Ozempic) and{" "}
                  <Link to="/medical-weight-loss/tirzepatide" className="text-brand-mid underline">tirzepatide</Link>{" "}
                  (Zepbound, Mounjaro). With the shortages resolved, mass-compounded versions are no longer permitted, and compounded products were never FDA-reviewed for purity or dosing accuracy. See our semaglutide and tirzepatide pages before considering an unregulated source.
                </p>
              </div>

              <div id="meds-wegovy-pill-vs-injection" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Should I choose the Wegovy pill or the injection?{" "}
                  <a
                    href="#meds-wegovy-pill-vs-injection"
                    aria-label="Link to this question: Should I choose the Wegovy pill or the injection?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  Both produce comparable average weight loss in trials. The once-weekly injection has the longest track record; the once-daily pill, taken on an empty stomach, suits patients who prefer to avoid needles, and insurance coverage often differs between the two.{" "}
                  <Link to="/medical-weight-loss/semaglutide" className="text-brand-mid underline">Our semaglutide page compares them in detail.</Link>{" "}
                </p>
              </div>

              <div id="meds-semaglutide-vs-tirzepatide" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Semaglutide or tirzepatide — which is better?{" "}
                  <a
                    href="#meds-semaglutide-vs-tirzepatide"
                    aria-label="Link to this question: Semaglutide or tirzepatide — which is better?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  In the only head-to-head trial (SURMOUNT-5), tirzepatide produced greater average weight loss than{" "}
                  <Link to="/medical-weight-loss/semaglutide" className="text-brand-mid underline">semaglutide</Link>{" "}
                  , 20.2% versus 13.7% at 72 weeks. But the better choice depends on insurance, tolerability, and health profile — semaglutide has the strongest cardiovascular-protection evidence. We prescribe both; see our semaglutide and{" "}
                  <Link to="/medical-weight-loss/tirzepatide" className="text-brand-mid underline">tirzepatide</Link>{" "}
                  pages.
                </p>
              </div>

              <div id="meds-zepbound-sleep-apnea" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Does Zepbound really treat sleep apnea?{" "}
                  <a
                    href="#meds-zepbound-sleep-apnea"
                    aria-label="Link to this question: Does Zepbound really treat sleep apnea?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  Yes. Tirzepatide is FDA-approved for moderate-to-severe obstructive sleep apnea in adults with obesity — the first medication approved for OSA. Mention snoring or CPAP use at your visit; it may also strengthen your insurance approval.{" "}
                  <Link to="/medical-weight-loss/tirzepatide" className="text-brand-mid underline">Our tirzepatide page covers the trial findings.</Link>{" "}
                </p>
              </div>

              <div id="meds-glp1-after-surgery" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Can I take weight-loss medication after bariatric surgery?{" "}
                  <a
                    href="#meds-glp1-after-surgery"
                    aria-label="Link to this question: Can I take weight-loss medication after bariatric surgery?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  Yes. GLP-1 medications are frequently used after a gastric sleeve or gastric bypass to treat weight regain or a plateau, and a bariatric surgeon can also evaluate whether anatomy is contributing. Dr. Fitzer manages both the surgical and medical sides of care.
                </p>
              </div>

              <div id="meds-oral-phentermine-safe" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Is phentermine safe?
                  {" "}
                  <a
                    href="#meds-oral-phentermine-safe"
                    aria-label="Link to this question: Is phentermine safe?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  For appropriately selected patients under physician supervision, yes — it has been
                  prescribed for over 60 years. It is not safe for patients with heart disease,
                  uncontrolled high blood pressure, hyperthyroidism, or glaucoma, or during
                  pregnancy, which is why we screen carefully, monitor blood pressure, and schedule
                  regular follow-ups rather than simply refilling prescriptions.
                </p>
              </div>

              <div id="meds-oral-how-much-loss" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  How much weight can I lose on phentermine or Contrave?
                  {" "}
                  <a
                    href="#meds-oral-how-much-loss"
                    aria-label="Link to this question: How much weight can I lose on phentermine or Contrave?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  In clinical studies, average weight loss is about 5% of body weight — roughly 12–15
                  pounds for a 250-pound patient — with some patients losing considerably more.
                  Results depend heavily on pairing the medication with nutrition changes, which is
                  why our program includes dietitian support rather than medication alone.
                </p>
              </div>

              <div id="meds-oral-vs-glp1" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Do these medications work as well as Ozempic or Zepbound?
                  {" "}
                  <a
                    href="#meds-oral-vs-glp1"
                    aria-label="Link to this question: Do these medications work as well as Ozempic or Zepbound?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  On average, no — GLP-1 medications produce roughly two to four times more weight
                  loss in trials. But the older medications cost a fraction as much, are more often
                  covered by insurance, and are sometimes the better medical fit. For many patients
                  they deliver exactly the result they need; for others they&apos;re a starting
                  point, and we can escalate if warranted.{" "}
                  <Link
                    to="/medical-weight-loss/oral-medications"
                    className="text-brand-mid underline"
                  >
                    Read our full guide to oral weight-loss medications.
                  </Link>
                </p>
              </div>

              <div id="meds-oral-phentermine-vs-diethylpropion" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  What&apos;s the difference between phentermine and diethylpropion?
                  {" "}
                  <a
                    href="#meds-oral-phentermine-vs-diethylpropion"
                    aria-label="Link to this question: What's the difference between phentermine and diethylpropion?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  They work the same way — both are stimulant-class appetite suppressants — but
                  diethylpropion is generally milder, with gentler effects on sleep, jitteriness, and
                  heart rate. Phentermine is usually the first choice; diethylpropion is our go-to
                  alternative when phentermine isn&apos;t well tolerated.
                </p>
              </div>

              <div id="meds-oral-online-sites" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Can I just get phentermine from an online weight-loss site?
                  {" "}
                  <a
                    href="#meds-oral-online-sites"
                    aria-label="Link to this question: Can I just get phentermine from an online weight-loss site?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  Be careful. Phentermine is a controlled substance, and legitimate prescribing
                  requires a real medical evaluation, blood-pressure monitoring, and follow-up. A
                  prescriber who has never examined you can&apos;t safely rule out the cardiovascular
                  conditions that make stimulants dangerous. Our evaluations are thorough, and
                  telehealth follow-ups keep ongoing care convenient.
                </p>
              </div>

              <div id="meds-oral-which-is-right" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2 group">
                  Which weight-loss medication is right for me?
                  {" "}
                  <a
                    href="#meds-oral-which-is-right"
                    aria-label="Link to this question: Which weight-loss medication is right for me?"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-brand-mid no-underline transition-opacity"
                  >
                    #
                  </a>
                </h3>
                <p>
                  It depends on whether your challenge is hunger, cravings, or habits — and on your
                  health history, insurance, and goals. That&apos;s a 30-minute conversation, not a
                  quiz. Dr. Fitzer prescribes every FDA-approved weight-loss medication and performs
                  bariatric surgery, so the recommendation you get is the one most likely to work for
                  you.
                </p>
              </div>
            </FaqLoadMore>
          </section>
          <section>
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              About Bariatric Surgery
            </h2>

            <div className="space-y-6 text-foreground/80 leading-[1.6]">
              <div id="what-is-bariatric-surgery" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  What is bariatric surgery?
                </h3>
                <p>
                  Bariatric surgery is a group of surgical procedures that help people lose weight
                  by making changes to the digestive system. Depending on the procedure, surgery
                  may reduce the size of the stomach, reroute the digestive tract, or both. It is
                  typically recommended when diet, exercise, and medications have not produced
                  sufficient results in patients with obesity-related health conditions.
                </p>
              </div>

              <div id="what-types-of-bariatric-procedures-do-you-offer" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  What types of bariatric procedures do you offer?
                </h3>
                <p className="mb-3">
                  The evidence-backed procedures we offer include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Roux-en-Y Gastric Bypass (RYGB):</strong> The stomach is divided into a
                    small pouch and connected directly to the small intestine, bypassing most of
                    the stomach and part of the intestine.
                  </li>
                  <li>
                    <strong>Sleeve Gastrectomy:</strong> Approximately 75–80% of the stomach is
                    removed, leaving a narrow, sleeve-shaped stomach. See our full guide to{" "}
                    <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery</Link>.
                  </li>

                  <li>
                    <strong>Adjustable Gastric Band (Lap-Band):</strong> An inflatable band is
                    placed around the upper stomach to create a small pouch and slow food passage.
                  </li>
                  <li>
                    <strong>ESG (Endoscopic Sleeve Gastrectomy):</strong> A non-surgical,
                    endoscopic procedure that reduces stomach volume from inside using sutures,
                    with no external incisions.
                  </li>
                  <li>
                    <strong>Orbera&reg; Intragastric Balloon:</strong> A temporary, non-surgical weight
                    loss balloon placed endoscopically in the stomach and filled with saline to
                    promote earlier fullness.
                  </li>
                  <li>
                    <strong>Transoral Outlet Reduction:</strong> An endoscopic revision procedure
                    for patients who have regained weight after gastric bypass, reducing the size
                    of the gastric outlet.
                  </li>
                  <li>
                    <strong>Revisional Bariatric Surgery:</strong> Surgical correction or conversion
                    of a previous bariatric procedure for patients experiencing complications or
                    inadequate weight loss.
                  </li>
                </ul>
              </div>

              <div id="which-procedure-is-right-for-me" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Which procedure is right for me?
                </h3>
                <p>
                  The right procedure depends on your BMI, health conditions, eating habits, surgical
                  history, and personal goals. Our team will review your full medical history and work
                  with you to recommend the best option. There is no one-size-fits-all answer.
                </p>
              </div>

              <div id="who-qualifies-for-bariatric-surgery" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Who qualifies for bariatric surgery?
                </h3>
                <p className="mb-3">The current NIH criteria include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>BMI of 35 or higher, OR</li>
                  <li>
                    BMI of 30–34.9 with at least one obesity-related condition (e.g., type 2 diabetes,
                    sleep apnea, hypertension, or heart disease)
                  </li>
                  <li>History of failed attempts at non-surgical weight loss</li>
                  <li>Commitment to long-term lifestyle changes</li>
                </ul>
                <p className="mt-3">
                  Read the full{" "}
                  <Link to="/resources/qualifications" className="text-brand-mid underline">
                    bariatric surgery qualifications
                  </Link>{" "}
                  for details on insurance, age, and BMI thresholds.
                </p>
              </div>

              <div id="reasons-not-qualify" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Are there reasons I might not qualify?
                </h3>
                <p>
                  Yes. Factors that may disqualify a candidate include untreated severe psychiatric
                  illness, active substance use disorder, certain medical conditions that make surgery
                  too risky, an inability to commit to post-operative lifestyle requirements, or
                  pregnancy.
                </p>
              </div>

              <div id="age-limit" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Is there an age limit?
                </h3>
                <p>
                  Bariatric surgery is most common in adults aged 18–70, but it is also appropriate for
                  some older adults, who are evaluated on a case-by-case basis. Dr. Fitzer does not
                  perform surgery on patients younger than 18, but a small number of centers do offer
                  bariatric surgery to patients as young as 15.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              Before Surgery
            </h2>
            <div className="space-y-6 text-foreground/80 leading-[1.6]">

              <div id="pre-surgical-process" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  What does the pre-surgical process look like?
                </h3>
                <p className="mb-3">The process typically includes:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Initial consultation and evaluation</li>
                  <li>
                    Medical clearance by your own PCP and other specialists (e.g., cardiology) if
                    indicated
                  </li>
                  <li>Testing (Labs, EKG, imaging, sleep study if indicated, etc.)</li>
                  <li>Nutritional counseling and dietary education</li>
                  <li>Psychological evaluation</li>
                  <li>Insurance pre-authorization (if applicable)</li>
                  <li>A pre-operative liquid diet (usually begins three days before surgery)</li>
                </ul>
              </div>

              <div id="psychological-evaluation" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Why do I need a psychological evaluation?
                </h3>
                <p>
                  The evaluation ensures you have realistic expectations, a stable mental health
                  foundation, and the emotional readiness to commit to lifelong behavioral changes.
                  It is a standard and supportive part of the process — not a barrier.
                </p>
              </div>

              <div id="special-diet-before-surgery" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Do I have to follow a special diet before surgery?
                </h3>
                <p>
                  In our practice, we have patients follow a special liquid diet for the three days
                  preceding surgery. A three-day diet produces a reduction in liver size similar to
                  two weeks of a low-calorie protein shake diet that some practices use. Our dietitian
                  will review the specific plan with you.
                </p>
              </div>

              <div id="stop-medications" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Will I need to stop any medications before surgery?
                </h3>
                <p>
                  Some medications — including blood thinners, NSAIDs, and certain diabetes drugs —
                  do need to be stopped before surgery (bariatric or otherwise). Dr. Fitzer and/or his
                  team will review your meds list and give you specific instructions for each med.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              The Procedure
            </h2>
            <div className="space-y-6 text-foreground/80 leading-[1.6]">

              <div id="laparoscopic" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Is bariatric surgery performed laparoscopically?
                </h3>
                <p>
                  Yes, in essentially all cases. We use minimally invasive laparoscopic or
                  robotic-assisted techniques, which involve small incisions, a camera, and specialized
                  instruments. This results in less pain, reduced scarring, and faster recovery compared
                  to open surgery.
                </p>
              </div>

              <div id="surgery-duration" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  How long does the surgery take?
                </h3>
                <p>
                  Dr. Fitzer performs most procedures in one hour or less. Revisional surgery takes a
                  little longer. Individual factors (such as multiple prior operations or very extreme
                  obesity) can impact the operative time.
                </p>
              </div>

              <div id="general-anesthesia" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Will I be under general anesthesia?
                </h3>
                <p>
                  Yes. All bariatric procedures are performed under general anesthesia. You will be
                  completely asleep and feel nothing during the operation.
                </p>
              </div>

              <div id="hospital-stay" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  How long will I be in the hospital?
                </h3>
                <p>
                  Most patients stay one night for sleeve gastrectomy and gastric bypass. Lap-Band,
                  TORe, and ESG patients usually go home the same day. Discharge dates sometimes vary
                  based on Dr. Fitzer's assessment.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              Insurance &amp; Coverage
            </h2>

            <div className="space-y-6 text-foreground/80 leading-[1.6]">
              <div id="does-my-health-insurance-cover-bariatric-surgery" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Does my health insurance cover bariatric surgery?
                </h3>
                <p className="mb-3">
                  If you have government-provided health insurance (Medicare, Medicaid, Tricare,
                  etc.), then congratulations! You do have bariatric surgery coverage.
                </p>
                <p className="mb-3">
                  If you have private health insurance (e.g., Cigna, United, Aetna, BCBS...), you may
                  have coverage. All private insurers do offer bariatric surgery coverage, but not
                  every policy includes it, unfortunately. (These days, it seems about 50-50.) If
                  your policy does not include bariatric surgery coverage, our{" "}
                  <Link to="/self-pay-bariatric-surgery" className="text-brand-mid underline hover:no-underline">
                    self-pay bariatric surgery
                  </Link>{" "}
                  page lists published prices for each procedure.
                </p>
                <p>
                  Give us a call. We can help you find out if your policy includes bariatric surgery
                  coverage.
                </p>
              </div>

              <div id="are-the-qualifications-different-if-using-insurance" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Are the qualifications for bariatric surgery different if you are using insurance?
                </h3>
                <p className="mb-3">
                  Strangely, yes. Insurance companies adhere to the prior NIH guidelines on weight
                  loss surgery. According to those guidelines, the BMI requirement is over 40 in the
                  absence of comorbid medical disease, and over 35 with it.
                </p>
                <p className="mb-3">
                  Those guidelines are now more than thirty years old (published in 1991), and there
                  is broad consensus in the medical community that they are too exclusive. (Insurance
                  companies are the noteworthy exception to the consensus.)
                </p>
                <p>
                  If you are not using insurance, those older criteria do not apply &mdash; the
                  self-pay requirement is a BMI of 30 or higher. Our{" "}
                  <Link to="/self-pay-bariatric-surgery" className="text-brand-mid underline hover:no-underline">
                    self-pay bariatric surgery
                  </Link>{" "}
                  page explains how self-pay qualification and pricing work.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              Recovery
            </h2>

            <div className="space-y-6 text-foreground/80 leading-[1.6]">
              <div id="activity-restrictions" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  What are the activity restrictions during recovery in the first few weeks?
                </h3>
                <p>
                  There are two main restrictions: (1) no driving for five days, and (2) no lifting
                  more than 25 pounds for the first four weeks. As far as return to work, people who
                  work at a desk are usually off work for 1–2 weeks; people on their feet most of the
                  workday (e.g., teachers) average 2–3 weeks off work; people who do heavy lifting at
                  work (and for whom no light duty is available) must take four weeks off.
                </p>
              </div>

              <div id="how-much-pain" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  How much pain should I expect?
                </h3>
                <p>
                  It is usually not too bad. Mild to moderate soreness is common for the first day
                  or two after laparoscopic surgery. It usually subsides by post-op day two. Most
                  patients manage well with oral pain medication.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              Diet &amp; Nutrition After Surgery
            </h2>

            <div className="space-y-6 text-foreground/80 leading-[1.6]">
              <div id="what-can-i-eat-after-surgery" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  What will I be able to eat after surgery?
                </h3>
                <p>
                  Long-term, we prefer that patients eat small, balanced meals rich in protein —
                  typically 3 small meals per day. If the meals are eaten with proper bariatric
                  technique, odds are the patient will feel no desire to snack in between. Foods
                  high in sugar and fat are discouraged and may cause "dumping syndrome" in bypass
                  patients. We recommend that patients avoid foods or drinks containing sweeteners,
                  whether sugar-containing or zero-calorie.
                </p>
              </div>

              <div id="do-i-need-vitamins-forever" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Do I need to take vitamins forever?
                </h3>
                <p>
                  Yes, but you'd need to take the same ones if you lost similar weight just through
                  dieting. Virginia Bariatric will give you a specific regimen in writing well in
                  advance of surgery.
                </p>
              </div>

              <div id="what-is-dumping-syndrome" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  What is dumping syndrome?
                </h3>
                <p>
                  Dumping syndrome is what we call the unpleasant symptoms that can occur after
                  gastric bypass or sleeve surgery in response to foods really rich in refined sugar
                  or liquid fat (grease). It is the result of abnormal gut hormone release provoked
                  when food moves rapidly into the small intestine. Symptoms include nausea,
                  cramping, sweating, dizziness, and sometimes diarrhea. It usually can be avoided by
                  following dietary guidelines.
                </p>
              </div>

              <div id="will-i-ever-eat-normally-again" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Will I ever be able to eat normally again?
                </h3>
                <p>
                  It depends how we define &quot;normally.&quot; If normally means eating as quickly
                  as you could before surgery, the answer is a hard no. (You may rest assured that no
                  bariatric patient will ever be in serious contention at a hotdog eating contest.)
                  Your relationship with food will change, and you will settle into a new normal that
                  is just as satisfying. You will learn to eat more slowly. You will learn to really
                  consider how full you currently feel every time you contemplate taking an additional
                  bite. Most patients are able to enjoy most of the foods they love once they relearn
                  how to eat. Virginia Bariatric staff will help you develop an enjoyable and
                  sustainable eating life.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              Gastric Sleeve Surgery
            </h2>
            <div className="space-y-6 text-foreground/80 leading-[1.6]">
              <p className="text-center">
                Full details, outcomes data, and imaging examples are on our{" "}
                <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">
                  gastric sleeve surgery
                </Link>{" "}
                page.
              </p>

              <div id="sleeve-bypassed-intestine" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Is any part of my stomach or intestine bypassed?
                </h3>
                <p>
                  No. The sleeve removes part of the stomach but does not reroute the intestines.
                  Food follows its normal path, which is one reason nutrient absorption is better
                  preserved than with gastric bypass.
                </p>
              </div>

              <div id="sleeve-or-bypass" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Sleeve or bypass — which is better?
                </h3>
                <p>
                  Both are excellent operations with similar weight loss and diabetes remission. The
                  sleeve is simpler with fewer complications; bypass is often preferred for patients
                  with significant acid reflux or certain metabolic conditions. The best choice is
                  individual — see our{" "}
                  <Link
                    to="/services/bypass-vs-sleeve"
                    className="text-brand-deep underline hover:no-underline"
                  >
                    bypass vs. sleeve comparison
                  </Link>
                  .
                </p>
              </div>

              <div id="sleeve-reversible" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Can the sleeve be reversed?
                </h3>
                <p>
                  No. Because part of the stomach is permanently removed, the sleeve is not
                  reversible — though it can be converted to another procedure if needed in the
                  future.
                </p>
              </div>

              <div id="sleeve-recovery" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  How long is recovery after gastric sleeve surgery?
                </h3>
                <p>
                  Most patients stay overnight in the hospital and return to normal activities within
                  about four weeks. You will begin a liquid diet immediately and advance through
                  pureed, soft, and regular foods over the following eight weeks.
                </p>
              </div>

              <div id="sleeve-insurance" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Is gastric sleeve covered by insurance?
                </h3>
                <p>
                  Many major insurers cover sleeve gastrectomy when medical criteria are met, such as
                  a BMI of 35 or higher, or 30 or higher with an obesity-related condition. Our team
                  verifies benefits and helps you through the authorization process. If you are
                  covering the cost yourself, our{" "}
                  <Link to="/self-pay-bariatric-surgery" className="text-brand-mid underline hover:no-underline">
                    self-pay bariatric surgery
                  </Link>{" "}
                  page lists the published sleeve price.
                </p>
              </div>

              <div id="sleeve-diet" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  What diet do I follow after sleeve gastrectomy?
                </h3>
                <p>
                  Recovery eating progresses in stages: clear liquids, full liquids and protein
                  shakes, pureed and soft foods, and finally regular solid foods. Daily vitamin
                  supplementation begins right away, and our dietitian supports you through each
                  stage.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              Endoscopic Sleeve Gastroplasty
            </h2>
            <div className="space-y-6 text-foreground/80 leading-[1.6]">
              <div id="esg-vs-gastric-sleeve" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Is ESG the same as gastric sleeve surgery?
                </h3>
                <p>
                  No. Surgical <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery</Link> laparoscopically removes about 80% of the stomach.

                  ESG reshapes the stomach with internal sutures through the mouth—nothing is removed
                  and there are no incisions. Surgery typically produces greater weight loss, while ESG
                  offers a lower-risk, incisionless, potentially reversible alternative.
                </p>
              </div>

              <div id="esg-vs-medications" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  How does ESG compare to weight-loss medications?
                </h3>
                <p>
                  Medications such as GLP-1 agonists can be highly effective but require ongoing use,
                  and weight is often regained when they are stopped. One 2024 economic analysis found
                  ESG to be more cost-effective than semaglutide over five years for class II obesity,
                  with greater sustained weight loss. Many patients use these approaches in
                  combination—this is best discussed individually.
                </p>
              </div>

              <div id="esg-reversible" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Is the procedure reversible?
                </h3>
                <p>
                  Because no tissue is removed, ESG is generally considered reversible or revisable,
                  which preserves the option of other treatments in the future.
                </p>
              </div>

              <div id="esg-results" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  How long until I see results?
                </h3>
                <p>
                  Weight loss typically begins within the first weeks and continues over 6 to 12
                  months, with results best sustained through ongoing nutrition and activity support.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              Orbera&reg; Balloon
            </h2>
            <div className="space-y-6 text-foreground/80 leading-[1.6]">
              <div id="orbera-vs-sleeve-esg" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  How is the balloon different from gastric sleeve or ESG?
                </h3>
                <p>
                  ORBERA&reg; is a temporary, removable device placed without incisions and taken out
                  after six months—nothing about your stomach is permanently changed. Sleeve
                  gastrectomy surgically removes part of the stomach, and endoscopic sleeve
                  gastroplasty reshapes it with internal sutures. Those procedures generally produce
                  more weight loss; the balloon offers a lower-commitment, fully reversible starting
                  point.
                </p>
              </div>

              <div id="orbera-permanent" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Does the balloon stay in permanently?
                </h3>
                <p>
                  No. ORBERA&reg; is designed to remain in the stomach for six months and is then removed
                  endoscopically. Leaving it in longer than recommended increases the risk of
                  deflation and is not advised.
                </p>
              </div>

              <div id="orbera-feel" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Will I feel the balloon?
                </h3>
                <p>
                  Most patients adjust within the first one to two weeks. Nausea and a sensation of
                  fullness are common at first and typically settle as your stomach accommodates the
                  balloon.
                </p>
              </div>

              <div id="orbera-after-removal" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  What happens after the balloon is removed?
                </h3>
                <p>
                  The goal is that the habits built during the 12-month program carry your results
                  forward. Continued nutrition support, activity, and follow-up are key to maintaining
                  weight loss, and your team will help you plan for this from the start.
                </p>
              </div>
            </div>
          </section>



          <section id="revisions" className="scroll-mt-24">
            <h2 className="text-2xl md:text-3xl font-serif text-center mb-6">
              Revisions
            </h2>
            <div className="space-y-6 text-foreground/80 leading-[1.6]">
              <div id="revision-failed" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Does needing a revision mean my first surgery failed, or I failed?
                </h3>
                <p>
                  Neither. For one thing, there are several acquired conditions that can cause a
                  once-successful bariatric procedure to lose its effectiveness. For instance, a gastric
                  bypass pouch can reconnect with the rest of the stomach (gastro-gastric fistula).
                  Pregnancy sometimes causes pouch or sleeve dilation that can result in major weight
                  regain. These problems can be fixed. In any event, obesity is a chronic disease that
                  resists treatment. The weight loss produced by bariatric surgery is durable for the
                  majority, but the need for a revision is not uncommon. It&apos;s expected in a subset
                  of patients and should not be taken as an indicator of personal failure. It&apos;s a
                  matter of biology and anatomy.
                </p>
              </div>

              <div id="revision-reflux" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Will a revision help my acid reflux?
                </h3>
                <p>
                  It sure can. Severe reflux after a sleeve gastrectomy is one of the best established
                  reasons for revision, and conversion to a Roux-en-Y gastric bypass is highly
                  effective at resolving it. It usually turns reflux off like a switch, in fact, with
                  the added benefit of producing further weight loss for most people.
                </p>
              </div>

              <div id="revision-risk" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Is revisional surgery riskier than my first operation?
                </h3>
                <p>
                  Possibly. Operating on altered anatomy with scar tissue adds complexity, so complication
                  rates seen in the national databases are modestly higher than for first-time surgery.
                  An experienced bariatric surgeon can perform revision procedures very safely. (Dr.
                  Fitzer has been performing revision surgery for more than twenty years.)
                </p>
              </div>

              <div id="revision-alternatives" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  Could medications or an endoscopic procedure work instead?
                </h3>
                <p>
                  Sometimes they can. For certain patients, weight-loss medications or an incisionless
                  endoscopic revision can provide an excellent solution. The right approach depends on
                  your anatomy, goals, and health—the things your evaluation will determine.
                </p>
              </div>

              <div id="revision-weight-loss" className="scroll-mt-24">
                <h3 className="text-lg md:text-xl font-semibold mb-2">
                  How much weight can I expect to lose?
                </h3>
                <p>
                  It varies with the procedure and your reason for revision. As a guide, sleeve-to-bypass
                  conversion averages roughly 20% total body weight loss at one year, while more
                  malabsorptive conversions can achieve more. Dr. Fitzer will give you a realistic
                  estimate.
                </p>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Contact Us
          </Link>
          <Link
            to="/resources"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Back to Resources
          </Link>
        </div>
      </PageSection>
    </>
  );
}
